import { axiosInstance } from '../axios.config';
import {
  NotificationDto,
  NotificationsResponseDto,
  UnreadCountResponseDto,
  GetNotificationsOptions,
} from '../types/notification.types';

// Notification types from backend
export const NotificationType = {
  BUY_BOX_MATCH: 'BUY_BOX_MATCH',
  NEW_BID: 'NEW_BID',
  BID_ACCEPTED: 'BID_ACCEPTED',
  BID_DECLINED: 'BID_DECLINED',
  BID_COUNTERED: 'BID_COUNTERED',
  BID_MESSAGE: 'BID_MESSAGE',
  BID_WON: 'BID_WON',
  BID_RECEIVED: 'BID_RECEIVED',
  CONNECTION_REQUEST: 'CONNECTION_REQUEST',
  CONNECTION_ACCEPTED: 'CONNECTION_ACCEPTED',
  NETWORK_LISTING: 'NETWORK_LISTING',
  LISTING_TAG: 'LISTING_TAG',
  LISTING_BACK_ON_MARKET: 'LISTING_BACK_ON_MARKET',
  NEW_MESSAGE: 'NEW_MESSAGE',
} as const;

export type NotificationType = typeof NotificationType[keyof typeof NotificationType];

export const DeliveryMode = {
  INSTANT: 'INSTANT',
  DIGEST: 'DIGEST',
} as const;

export type DeliveryMode = typeof DeliveryMode[keyof typeof DeliveryMode];

// Single preference item as returned by GET /notifications/preferences
export interface NotificationPreferenceItem {
  notificationType: NotificationType;
  emailEnabled: boolean;
  deliveryMode: DeliveryMode;
}

// Response from GET /notifications/preferences (array of preferences)
export type NotificationPreferencesResponse = NotificationPreferenceItem[];

// Payload for PUT /notifications/preferences
export interface UpdateNotificationPreferencesPayload {
  preferences: {
    notificationType: NotificationType;
    emailEnabled: boolean;
    deliveryMode?: DeliveryMode;
  }[];
}

// Helper type for flat boolean representation used in UI
export interface NotificationPreferencesFlat {
  emailConnectionRequest: boolean;
  emailConnectionAccepted: boolean;
  emailListingTag: boolean;
  emailNetworkListing: boolean;
}

// Mapping from flat keys to NotificationType
export const flatKeyToNotificationType: Record<keyof NotificationPreferencesFlat, NotificationType> = {
  emailConnectionRequest: NotificationType.CONNECTION_REQUEST,
  emailConnectionAccepted: NotificationType.CONNECTION_ACCEPTED,
  emailListingTag: NotificationType.LISTING_TAG,
  emailNetworkListing: NotificationType.NETWORK_LISTING,
};

// Legacy notification type for backward compatibility
export interface Notification {
  id: string;
  userId: string;
  type: string;
  entityType: string;
  entityId: string;
  message: string;
  metadata: Record<string, unknown> | null;
  isRead: boolean;
  createdAt: string;
}

class NotificationsService {
  /**
   * Get notifications for authenticated user (with pagination)
   */
  async getNotifications(
    options: GetNotificationsOptions = {},
  ): Promise<NotificationsResponseDto> {
    const params = new URLSearchParams();

    if (options.type) {
      params.append('type', options.type);
    }
    if (options.isRead !== undefined) {
      params.append('isRead', String(options.isRead));
    }
    if (options.page) {
      params.append('page', options.page.toString());
    }
    if (options.limit) {
      params.append('limit', options.limit.toString());
    }

    const queryString = params.toString();
    const url = queryString ? `/notifications?${queryString}` : '/notifications';

    const response = await axiosInstance.get<NotificationsResponseDto>(url);
    return response.data;
  }

  /**
   * Get all notifications for the current user (legacy)
   */
  async getAll(): Promise<Notification[]> {
    const response = await axiosInstance.get<Notification[]>('/notifications');
    return response.data;
  }

  /**
   * Get unread notifications for the current user (legacy)
   */
  async getUnread(): Promise<Notification[]> {
    const response = await axiosInstance.get<Notification[]>('/notifications/unread');
    return response.data;
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const response =
      await axiosInstance.get<UnreadCountResponseDto>('/notifications/unread-count');
    return response.data.count;
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<NotificationDto> {
    const response = await axiosInstance.put<NotificationDto>(
      `/notifications/${notificationId}/read`,
    );
    return response.data;
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    await axiosInstance.put('/notifications/mark-all-read');
  }

  /**
   * Get notification preferences for the current user (raw API response)
   */
  async getPreferences(): Promise<NotificationPreferencesResponse> {
    const response = await axiosInstance.get<NotificationPreferencesResponse>('/notifications/preferences');
    return response.data;
  }

  /**
   * Get notification preferences as flat object for UI
   */
  async getPreferencesFlat(): Promise<NotificationPreferencesFlat> {
    const prefs = await this.getPreferences();
    return this.convertToFlat(prefs);
  }

  /**
   * Update notification preferences for the current user
   */
  async updatePreferences(
    data: UpdateNotificationPreferencesPayload
  ): Promise<{ message: string }> {
    const response = await axiosInstance.put<{ message: string }>(
      '/notifications/preferences',
      data
    );
    return response.data;
  }

  /**
   * Update a single preference by flat key (for UI toggle)
   */
  async updatePreferenceByKey(
    key: keyof NotificationPreferencesFlat,
    enabled: boolean
  ): Promise<{ message: string }> {
    const notificationType = flatKeyToNotificationType[key];
    return this.updatePreferences({
      preferences: [{ notificationType, emailEnabled: enabled }]
    });
  }

  /**
   * Convert API response array to flat object for UI
   */
  convertToFlat(prefs: NotificationPreferencesResponse): NotificationPreferencesFlat {
    const flat: NotificationPreferencesFlat = {
      emailConnectionRequest: false,
      emailConnectionAccepted: false,
      emailListingTag: false,
      emailNetworkListing: false,
    };

    for (const pref of prefs) {
      switch (pref.notificationType) {
        case NotificationType.CONNECTION_REQUEST:
          flat.emailConnectionRequest = pref.emailEnabled;
          break;
        case NotificationType.CONNECTION_ACCEPTED:
          flat.emailConnectionAccepted = pref.emailEnabled;
          break;
        case NotificationType.LISTING_TAG:
          flat.emailListingTag = pref.emailEnabled;
          break;
        case NotificationType.NETWORK_LISTING:
          flat.emailNetworkListing = pref.emailEnabled;
          break;
      }
    }

    return flat;
  }

  /**
   * Reset notification preferences to defaults
   */
  async resetPreferences(): Promise<{ message: string }> {
    const response = await axiosInstance.post<{ message: string }>(
      '/notifications/preferences/reset'
    );
    return response.data;
  }

  /**
   * Get digest info (last digest sent timestamp)
   */
  async getDigestInfo(): Promise<{ lastDigestSentAt: Date | null }> {
    const response = await axiosInstance.get<{ lastDigestSentAt: Date | null }>(
      '/notifications/digest-info'
    );
    return response.data;
  }
}

// Export singleton instance
export const notificationsService = new NotificationsService();
export default notificationsService;
